import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  Grid,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Radio,
  Image as ImageIcon,
  PhotoCamera,
  Cancel,
  Save
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { programService } from '../../services/programService';
import { useAuth } from '../../contexts/AuthContext';

const DJProgramManagement = () => {
  const { user } = useAuth();
  const [openDialog, setOpenDialog] = useState(false);
  const [bannerDialog, setBannerDialog] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    day_of_week: 'monday',
    start_time: '',
    end_time: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const queryClient = useQueryClient();

  // Função para tratar mensagens de erro de forma amigável
  const getErrorMessage = (error) => {
    const errorMessage = error.response?.data?.error || error.message || 'Erro desconhecido';
    console.log('🔍 DJ getErrorMessage - Error message:', errorMessage);
    
    // Mensagens específicas para diferentes tipos de erro
    if (errorMessage.includes('Conflito de horário')) {
      let friendlyMessage;
      if (errorMessage.includes('outro programa de DJ')) {
        friendlyMessage = '⚠️ Conflito de horário detectado! Já existe outro programa de DJ ao vivo ativo neste horário. Por favor, escolha um horário diferente.';
      } else {
        friendlyMessage = '⚠️ Conflito de horário detectado! Já existe outro programa de DJ ao vivo ativo neste horário. Por favor, escolha um horário diferente.';
      }
      console.log('🔍 DJ getErrorMessage - Returning friendly message:', friendlyMessage);
      return friendlyMessage;
    }
    
    if (errorMessage.includes('horário')) {
      return '⚠️ Erro de horário: Verifique se os horários de início e fim estão corretos.';
    }
    
    if (errorMessage.includes('DJ')) {
      return '⚠️ Erro relacionado ao DJ: Verifique se o DJ selecionado está ativo.';
    }
    
    if (errorMessage.includes('nome')) {
      return '⚠️ Erro no nome: Verifique se o nome do programa não está duplicado.';
    }
    
    // Mensagem padrão para outros erros
    return `❌ ${errorMessage}`;
  };

  // Buscar programas do DJ logado
  const { data: programsData, isLoading: programsLoading } = useQuery({
    queryKey: ['programs', user?.id],
    queryFn: () => programService.getAllPrograms({ dj_id: user?.id }),
  });

  const programs = programsData?.data?.programs || [];

  // Mutations
  const createProgramMutation = useMutation({
    mutationFn: (data) => programService.createProgram(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['programs']);
      const message = response.data.requiresApproval 
        ? 'Programa criado com sucesso! Aguarde aprovação do administrador para que seja exibido no site.'
        : 'Programa criado com sucesso!';
      setSnackbar({ open: true, message, severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      console.log('🔍 DJ createProgramMutation onError - Error:', error);
      const friendlyMessage = getErrorMessage(error);
      console.log('🔍 DJ createProgramMutation onError - Setting snackbar with message:', friendlyMessage);
      setSnackbar({ open: true, message: friendlyMessage, severity: 'error' });
    }
  });

  const updateProgramMutation = useMutation({
    mutationFn: ({ id, data }) => programService.updateProgram(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['programs']);
      setSnackbar({ open: true, message: 'Programa atualizado com sucesso!', severity: 'success' });
      handleCloseDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    }
  });

  const deleteProgramMutation = useMutation({
    mutationFn: (id) => programService.deleteProgram(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['programs']);
      setSnackbar({ open: true, message: 'Programa deletado com sucesso!', severity: 'success' });
    },
    onError: (error) => {
      setSnackbar({ open: true, message: getErrorMessage(error), severity: 'error' });
    }
  });

  const uploadBannerMutation = useMutation({
    mutationFn: ({ programId, formData }) => programService.uploadBanner(programId, formData),
    onSuccess: () => {
      queryClient.invalidateQueries(['programs']);
      setSnackbar({ open: true, message: 'Banner atualizado com sucesso!', severity: 'success' });
      handleCloseBannerDialog();
    },
    onError: (error) => {
      setSnackbar({ open: true, message: error.response?.data?.error || 'Erro ao fazer upload do banner', severity: 'error' });
    }
  });

  const handleOpenDialog = (program = null) => {
    if (program) {
      setEditingProgram(program);
      setFormData({
        name: program.name || '',
        description: program.description || '',
        day_of_week: program.day_of_week || 'monday',
        start_time: program.start_time || '',
        end_time: program.end_time || ''
      });
    } else {
      setEditingProgram(null);
      setFormData({
        name: '',
        description: '',
        day_of_week: 'monday',
        start_time: '',
        end_time: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingProgram(null);
    setFormData({
      name: '',
      description: '',
      day_of_week: 'monday',
      start_time: '',
      end_time: ''
    });
  };

  const handleSubmit = () => {
    if (editingProgram) {
      updateProgramMutation.mutate({ id: editingProgram.id, data: formData });
    } else {
      createProgramMutation.mutate(formData);
    }
  };

  const handleDelete = (id) => {
    if (window.confirm('Tem certeza que deseja deletar este programa?')) {
      deleteProgramMutation.mutate(id);
    }
  };

  const handleOpenBannerDialog = (program) => {
    setSelectedProgram(program);
    setSelectedFile(null);
    setPreviewUrl(program.banner_image || null);
    setBannerDialog(true);
  };

  const handleCloseBannerDialog = () => {
    setBannerDialog(false);
    setSelectedProgram(null);
    setSelectedFile(null);
    setPreviewUrl(null);
    // Limpar o input de arquivo
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        setSnackbar({ 
          open: true, 
          message: 'Formato de arquivo inválido. Use apenas JPEG, JPG ou PNG.', 
          severity: 'error' 
        });
        // Limpar o input
        event.target.value = '';
        return;
      }

      // Validar tamanho (máximo 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setSnackbar({ 
          open: true, 
          message: 'Arquivo muito grande. Tamanho máximo: 5MB.', 
          severity: 'error' 
        });
        // Limpar o input
        event.target.value = '';
        return;
      }

      setSelectedFile(file);
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
    }
  };

  const handleUploadBanner = () => {
    if (selectedFile && selectedProgram) {
      const formData = new FormData();
      formData.append('banner', selectedFile);
      uploadBannerMutation.mutate({ programId: selectedProgram.id, formData });
    }
  };

  const getDayLabel = (day) => {
    const days = {
      monday: 'Segunda-feira',
      tuesday: 'Terça-feira',
      wednesday: 'Quarta-feira',
      thursday: 'Quinta-feira',
      friday: 'Sexta-feira',
      saturday: 'Sábado',
      sunday: 'Domingo'
    };
    return days[day] || day;
  };

  const getStatusColor = (isActive) => {
    return isActive ? 'success' : 'error';
  };

  const getStatusLabel = (isActive) => {
    return isActive ? 'Ativo' : 'Inativo';
  };

  const formatTime = (time) => {
    if (!time) return '';
    // Remove seconds from time string (HH:MM:SS -> HH:MM)
    return time.substring(0, 5);
  };

  if (programsLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress sx={{ color: '#FF6B35' }} />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" sx={{ fontFamily: 'Orbitron', fontWeight: 700, color: '#FF6B35' }}>
              Meus Programas
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                bgcolor: '#FF6B35',
                '&:hover': { bgcolor: '#FF8A65' }
              }}
            >
              Adicionar Programa
            </Button>
          </Box>

          {programs.length === 0 ? (
            <Box textAlign="center" py={4}>
              <Radio sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 2 }} />
              <Typography variant="h6" sx={{ opacity: 0.7, mb: 1 }}>
                Nenhum programa cadastrado
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.5 }}>
                Clique em "Adicionar Programa" para criar seu primeiro programa
              </Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Nome do Programa</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell>Dia da Semana</TableCell>
                    <TableCell>Horário</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Banner</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {programs.map((program) => (
                    <TableRow key={program.id}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {program.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ opacity: 0.7, maxWidth: 200 }}>
                          {program.description || 'Sem descrição'}
                        </Typography>
                      </TableCell>
                      <TableCell>{getDayLabel(program.day_of_week)}</TableCell>
                      <TableCell>
                        {formatTime(program.start_time)} - {formatTime(program.end_time)}
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Chip
                            label={getStatusLabel(program.is_active)}
                            color={getStatusColor(program.is_active)}
                            size="small"
                          />
                          {!program.is_active && (
                            <Chip
                              label="Aguardando Aprovação"
                              color="warning"
                              size="small"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {program.banner_image ? (
                            <Avatar
                              src={program.banner_image}
                              variant="rounded"
                              sx={{ width: 40, height: 40 }}
                            />
                          ) : (
                            <Avatar
                              variant="rounded"
                              sx={{ width: 40, height: 40, bgcolor: 'rgba(255, 255, 255, 0.1)' }}
                            >
                              <ImageIcon />
                            </Avatar>
                          )}
                          <Button
                            size="small"
                            startIcon={<ImageIcon />}
                            onClick={() => handleOpenBannerDialog(program)}
                            sx={{ color: '#00D4FF', minWidth: 'auto' }}
                          >
                            {program.banner_image ? 'Alterar' : 'Adicionar'}
                          </Button>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" gap={1}>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(program)}
                              sx={{ color: '#00D4FF' }}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Deletar">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(program.id)}
                              sx={{ color: '#FF1744' }}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog para Editar/Criar Programa */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingProgram ? 'Editar Programa' : 'Adicionar Programa'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            {editingProgram ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Atenção:</strong> O horário e dia da semana não podem ser alterados após a criação do programa.
                  Entre em contato com o administrador se precisar fazer alterações nesses campos.
                </Typography>
              </Alert>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Nota:</strong> Apenas o administrador poderá alterar o dia e horário após a criação do programa.
                </Typography>
              </Alert>
            )}

            {!editingProgram && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Importante:</strong> Após criar o programa, ele será enviado para revisão do administrador. 
                  O programa só será exibido no site após aprovação.
                </Typography>
              </Alert>
            )}

            <TextField
              fullWidth
              label="Nome do Programa *"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
              helperText="Ex: Show da Manhã"
            />
            
            <TextField
              fullWidth
              label="Descrição"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
              helperText="Descreva o conteúdo e estilo do seu programa"
            />

            {editingProgram ? (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0, 0, 0, 0.1)', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.7, mb: 1 }}>
                  Informações do horário (não editáveis):
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      Dia da Semana:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {getDayLabel(editingProgram.day_of_week)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                      Horário:
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      {formatTime(editingProgram.start_time)} - {formatTime(editingProgram.end_time)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Dia da Semana *</InputLabel>
                  <Select
                    value={formData.day_of_week}
                    onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                    label="Dia da Semana *"
                    required
                  >
                    <MenuItem value="monday">Segunda-feira</MenuItem>
                    <MenuItem value="tuesday">Terça-feira</MenuItem>
                    <MenuItem value="wednesday">Quarta-feira</MenuItem>
                    <MenuItem value="thursday">Quinta-feira</MenuItem>
                    <MenuItem value="friday">Sexta-feira</MenuItem>
                    <MenuItem value="saturday">Sábado</MenuItem>
                    <MenuItem value="sunday">Domingo</MenuItem>
                  </Select>
                </FormControl>
                
                <Box display="flex" gap={2} margin="normal">
                  <TextField
                    label="Horário de Início *"
                    type="time"
                    value={formData.start_time}
                    onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                    required
                  />
                  <TextField
                    label="Horário de Fim *"
                    type="time"
                    value={formData.end_time}
                    onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    sx={{ flex: 1 }}
                    required
                  />
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createProgramMutation.isPending || updateProgramMutation.isPending}
            sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#FF8A65' } }}
          >
            {(createProgramMutation.isPending || updateProgramMutation.isPending) ? 
              <CircularProgress size={20} /> : 'Salvar'
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog para Upload de Banner */}
      <Dialog open={bannerDialog} onClose={handleCloseBannerDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedProgram?.banner_image ? 'Alterar Banner do Programa' : 'Adicionar Banner do Programa'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Dimensões ideais:</strong> 1229px de largura x 394px de altura<br/>
                <strong>Formatos aceitos:</strong> JPEG, JPG, PNG<br/>
                <strong>Tamanho máximo:</strong> 5MB
              </Typography>
            </Alert>

            <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
              {previewUrl ? (
                <Box>
                  <Typography variant="body2" sx={{ mb: 1, textAlign: 'center' }}>
                    Preview do Banner:
                  </Typography>
                  <Box
                    component="img"
                    src={previewUrl}
                    alt="Preview do banner"
                    sx={{
                      maxWidth: '100%',
                      maxHeight: 200,
                      objectFit: 'contain',
                      borderRadius: 1,
                      border: '1px solid rgba(255, 255, 255, 0.1)'
                    }}
                  />
                </Box>
              ) : (
                <Box textAlign="center">
                  <PhotoCamera sx={{ fontSize: 64, color: 'rgba(255, 255, 255, 0.3)', mb: 1 }} />
                  <Typography variant="body2" sx={{ opacity: 0.7 }}>
                    Nenhum banner selecionado
                  </Typography>
                </Box>
              )}

              <Button
                variant="outlined"
                component="label"
                startIcon={<PhotoCamera />}
                sx={{ borderColor: '#FF6B35', color: '#FF6B35' }}
              >
                Selecionar Banner
                <input
                  type="file"
                  hidden
                  accept="image/jpeg,image/jpg,image/png"
                  onChange={handleFileSelect}
                />
              </Button>

              {selectedFile && (
                <Typography variant="body2" sx={{ opacity: 0.7, textAlign: 'center' }}>
                  Arquivo selecionado: {selectedFile.name}<br/>
                  Tamanho: {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleCloseBannerDialog}
            startIcon={<Cancel />}
            disabled={uploadBannerMutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleUploadBanner}
            variant="contained"
            disabled={!selectedFile || uploadBannerMutation.isPending}
            startIcon={uploadBannerMutation.isPending ? <CircularProgress size={20} /> : <Save />}
            sx={{ bgcolor: '#FF6B35', '&:hover': { bgcolor: '#FF8A65' } }}
          >
            {uploadBannerMutation.isPending ? 'Enviando...' : 'Salvar Banner'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar para feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ 
          zIndex: 9999,
          '& .MuiSnackbarContent-root': {
            backgroundColor: snackbar.severity === 'error' ? '#d32f2f' : '#2e7d32'
          }
        }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            fontSize: '1rem',
            fontWeight: 500,
            '& .MuiAlert-message': {
              whiteSpace: 'pre-line'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DJProgramManagement;
